// useUpdateTask.js

import { useMutation } from "@apollo/client";
import { UPDATE_TASK } from "../../graphql/mutations/ProjectEditContext";

/**
 * Custom hook to update a task using GraphQL mutation.
 *
 * @returns {[Function, Object]} A function to update the task and an object containing data, error, and loading states.
 */
const useUpdateTask = () => {
  const [mutation, { data, error, loading }] = useMutation(UPDATE_TASK);

  /**
   * Function to update a task with the provided parameters.
   *
   * @param {UpdateTaskParams} params - The task update parameters.
   * @returns {UpdateTaskResult|undefined} The result of the mutation.
   *
   * 1. If the mutation fails due to network or server issues, `undefined` is returned.
   * 2. If the mutation is executed but rejected by the server (e.g., task update failed),
   *    an `UpdateTaskResult` object with `success: false` and an `errorResult` is returned.
   */
  const updateTask = async ({ projectId, taskId, task }) => {
    const variables = {
      updateTaskParams: {
        projectId,
        taskId,
      },
      task,
    };

    const res = await mutation({ variables });

    // Return result object or undefined if the mutation fails (e.g., network error).
    return res?.data?.projects?.updateTask;
  };

  return [updateTask, { data, error, loading }];
};

/**
 * @typedef {Object} UpdateTaskParams
 * @property {number} projectId - The project ID.
 * @property {number} taskId - The task ID to update.
 * @property {Object} task - The task object with updated values.
 */

/**
 * @typedef {Object} UpdateTaskResult
 * @property {boolean} success - Indicates whether the task update was successful.
 * @property {Object|null} errorResult - Present only if the update failed. Contains details of the error.
 * @property {Object|undefined} task - Present only if the update was successful. Contains the updated task.
 */

/**
 * Exporting the custom hook for external usage.
 */
export { useUpdateTask };
